"use client";

// UX-LAB — Widget-Vokabular fuer das Reel.
//
// ENTSCHEIDENDER UNTERSCHIED zu den bisherigen Buehnen: kein Widget haelt eine
// eigene Uhr. Jedes bekommt `p` (0..1 lokaler Fortschritt) von der Reel-Uhr und
// leitet SEINEN Zustand daraus ab. Folge: die ganze Szene ist spulbar — man kann
// an jede Stelle springen und alles sieht korrekt aus. Mit Timern pro Widget
// waere das unmoeglich, und genau daran leiden die meisten solcher Animationen.
//
// Ausserdem: keine CSS-Keyframes fuer den Auftritt. Der Eintritt wird aus p
// gerechnet, damit er beim Spulen mitspringt statt neu zu starten.

import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";

export const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
export const easeOut = (x: number) => 1 - Math.pow(1 - clamp01(x), 3);
export const easeInOut = (x: number) =>
  clamp01(x) < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

/** Deterministisches Rauschen — same input, same output. Kein Math.random(),
 *  weil das beim Spulen flackern wuerde. */
export const noise = (i: number) => {
  const r = Math.sin(i * 12.9898) * 43758.5453;
  return r - Math.floor(r);
};

/** Fortschritt eines gestaffelten Elements innerhalb eines Widgets. */
export const stagger = (p: number, i: number, count: number, span = 0.55) => {
  const step = span / Math.max(1, count);
  return clamp01((p - i * step) / (1 - span + step));
};

// ── Gemeinsame Huelle ──────────────────────────────────────────────────
// Eintritt (0 → 0.18) und Quittung (ab 0.86) sind fuer ALLE Widgets gleich.
// Das ist der Grund, warum das Reel trotz elf verschiedener Inhalte wie ein
// System wirkt und nicht wie eine Sammlung.

export function Widget({
  app, Icon, title, p, children,
}: {
  app: string;
  Icon: LucideIcon;
  title: string;
  p: number;
  children?: React.ReactNode;
}) {
  const enter = easeOut(p / 0.18);
  const done = p > 0.86;
  return (
    <div
      className="rounded-[var(--radius)] border border-border bg-card p-3"
      style={{
        opacity: enter,
        transform: `translateY(${(1 - enter) * 16}px) scale(${0.955 + enter * 0.045})`,
        filter: `blur(${(1 - enter) * 5}px)`,
        // Dunkler Schatten (NICHT var(--foreground) — das ist im Dark-Mode fast
        // weiss und ergibt einen komischen weissen Glow zwischen gestapelten
        // Karten). Schwarz gibt in Hell dezente Tiefe, in Dunkel unsichtbar =
        // flach (Tiefe kommt dort ohnehin aus Rand + hellerer Card-Flaeche).
        boxShadow: "0 12px 30px -18px rgba(0, 0, 0, 0.45)",
      }}
    >
      <div className="flex items-center gap-2">
        <Icon size={13} className="shrink-0 text-muted-foreground" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/70">
          {app}
        </span>
        <span
          className="ml-auto flex h-4 w-4 items-center justify-center rounded-full bg-primary"
          style={{
            opacity: done ? 1 : 0,
            transform: `scale(${done ? 1 : 0.5})`,
            transition: "opacity 260ms ease, transform 260ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <Check size={9} strokeWidth={2.75} className="text-primary-foreground" />
        </span>
      </div>
      <p className="mt-1.5 text-sm font-medium leading-tight">{title}</p>
      {children}
    </div>
  );
}

// ══ 1 · KALENDER ═══════════════════════════════════════════════════════
// Der Zieltag wird nicht eingeblendet, sondern "gefunden": ein Ring wandert
// ueber die Woche und rastet ein.

export function WCalendar({
  p, app, Icon, title, day, slot,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; day: number; slot: string;
}) {
  const q = clamp01((p - 0.18) / 0.6);
  const days = [1, 2, 3, 4, 5, 6, 7];
  const cursor = Math.round(easeInOut(q) * (day - 1));
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 flex gap-1">
        {days.map((d, i) => {
          const isTarget = d === day;
          const hit = isTarget && q > 0.92;
          const under = i === cursor && q < 0.92;
          return (
            <span
              key={d}
              className="flex h-6 flex-1 items-center justify-center rounded-[5px] border text-[10px] tabular-nums transition-colors duration-200"
              style={{
                borderColor: hit || under ? "var(--foreground)" : "var(--border)",
                background: hit ? "var(--primary)" : "transparent",
                color: hit ? "var(--primary-foreground)" : undefined,
                opacity: hit || under ? 1 : 0.45,
              }}
            >
              {d + 14}
            </span>
          );
        })}
      </div>
      <p
        className="mt-2 font-mono text-[11px] text-muted-foreground"
        style={{ opacity: clamp01((p - 0.7) / 0.2) }}
      >
        {slot}
      </p>
    </Widget>
  );
}

// ══ 2 · DOKUMENT ═══════════════════════════════════════════════════════
// Zeilen "schreiben" sich: Breite waechst, danach kippt die Skelett-Linie in
// echten Text um. Das liest sich als Entstehen, nicht als Einblenden.

export function WDoc({
  p, app, Icon, title, lines,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; lines: string[];
}) {
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 space-y-1.5">
        {lines.map((l, i) => {
          const s = stagger(clamp01((p - 0.15) / 0.75), i, lines.length, 0.6);
          const w = easeOut(s / 0.6);
          const text = clamp01((s - 0.62) / 0.38);
          return (
            <div key={l} className="relative h-3">
              <span
                className="absolute left-0 top-1 block h-1.5 rounded bg-muted-foreground/25"
                style={{ width: `${w * (72 + noise(i) * 26)}%`, opacity: 1 - text }}
              />
              <span
                className="absolute left-0 top-0 block text-[11px] leading-[0.95rem] text-muted-foreground"
                style={{ opacity: text }}
              >
                {l}
              </span>
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 3 · MAIL ═══════════════════════════════════════════════════════════

export function WMail({
  p, app, Icon, title, to, body, sent,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; to: string; body: string; sent?: boolean;
}) {
  const q = clamp01((p - 0.2) / 0.55);
  const n = Math.floor(easeOut(q) * body.length);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <p className="mt-1.5 font-mono text-[10px] text-muted-foreground/70">An: {to}</p>
      <p className="mt-1 min-h-[2.1rem] text-[11px] leading-relaxed text-muted-foreground">
        {body.slice(0, n)}
        {n < body.length && p > 0.2 && (
          <span className="ml-px inline-block h-[0.9em] w-[2px] translate-y-[0.1em] bg-primary/70" />
        )}
      </p>
      <p
        className="mt-1.5 text-[10px] font-medium"
        style={{ opacity: clamp01((p - 0.82) / 0.14) }}
      >
        {sent ? "Verschickt" : "Wartet auf dein OK"}
      </p>
    </Widget>
  );
}

// ══ 4 · AUFGABEN ═══════════════════════════════════════════════════════

export function WTasks({
  p, app, Icon, title, items,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; items: string[];
}) {
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 space-y-1.5">
        {items.map((it, i) => {
          const s = stagger(clamp01((p - 0.18) / 0.72), i, items.length, 0.5);
          const box = easeOut(s / 0.5);
          const struck = clamp01((s - 0.55) / 0.45);
          return (
            <div
              key={it}
              className="flex items-center gap-2 text-[11px]"
              style={{ opacity: box, transform: `translateX(${(1 - box) * 8}px)` }}
            >
              <span
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border"
                style={{
                  borderColor: struck > 0.4 ? "transparent" : "var(--border)",
                  background: struck > 0.4 ? "var(--primary)" : "transparent",
                }}
              >
                <Check
                  size={10}
                  strokeWidth={2.5}
                  className="text-primary-foreground"
                  style={{ opacity: clamp01((struck - 0.4) / 0.3) }}
                />
              </span>
              <span className="relative text-muted-foreground">
                {it}
                <span
                  className="absolute left-0 top-1/2 block h-px bg-muted-foreground/50"
                  style={{ width: `${struck * 100}%` }}
                />
              </span>
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 5 · POSITIONS-TABELLE ══════════════════════════════════════════════
// Die Summe zaehlt nicht linear hoch, sondern springt mit jeder Zeile —
// so wie eine Summe tatsaechlich entsteht.

export function WTable({
  p, app, Icon, title, rows, currency = "€",
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  rows: { label: string; qty: string; value: number }[]; currency?: string;
}) {
  const q = clamp01((p - 0.16) / 0.68);
  const shown = rows.filter((_, i) => stagger(q, i, rows.length, 0.7) > 0.05);
  const sum = shown.reduce((a, r) => a + r.value, 0);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 space-y-1">
        {rows.map((r, i) => {
          const s = stagger(q, i, rows.length, 0.7);
          const e = easeOut(s / 0.45);
          return (
            <div
              key={r.label}
              className="flex items-baseline gap-2 text-[11px]"
              style={{ opacity: e, transform: `translateY(${(1 - e) * 6}px)` }}
            >
              <span className="truncate text-muted-foreground">{r.label}</span>
              <span className="font-mono text-[10px] text-muted-foreground/60">{r.qty}</span>
              <span className="ml-auto shrink-0 font-mono tabular-nums">
                {r.value.toLocaleString("de-DE")} {currency}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-baseline justify-between border-t border-border pt-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Summe</span>
        <span className="font-mono text-sm font-semibold tabular-nums">
          {sum.toLocaleString("de-DE")} {currency}
        </span>
      </div>
    </Widget>
  );
}

// ══ 6 · SPARKLINE ══════════════════════════════════════════════════════
// Die Linie zeichnet sich per stroke-dashoffset, der Wert zaehlt synchron.

export function WSpark({
  p, app, Icon, title, points, value, unit, caption,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  points: number[]; value: number; unit?: string; caption?: string;
}) {
  const q = easeOut(clamp01((p - 0.18) / 0.62));
  const max = Math.max(...points);
  const d = points
    .map((v, i) => `${(i / (points.length - 1)) * 200},${40 - (v / max) * 34}`)
    .join(" ");
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="font-mono text-lg font-semibold tabular-nums leading-none">
          {Math.round(value * q).toLocaleString("de-DE")}
        </span>
        {unit && <span className="text-[11px] text-muted-foreground">{unit}</span>}
        {caption && (
          <span className="ml-auto text-[10px] text-muted-foreground/70">{caption}</span>
        )}
      </div>
      <svg viewBox="0 0 200 44" className="mt-1 h-9 w-full" aria-hidden="true">
        <polyline
          points={d}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ strokeDasharray: 260, strokeDashoffset: 260 * (1 - q) }}
        />
      </svg>
    </Widget>
  );
}

// ══ 7 · STATUS-STEPPER ═════════════════════════════════════════════════

export function WStepper({
  p, app, Icon, title, steps,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; steps: string[];
}) {
  const q = clamp01((p - 0.2) / 0.62);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-3 flex items-center">
        {steps.map((s, i) => {
          const reach = clamp01((q - i / steps.length) * steps.length);
          return (
            <div key={s} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-1">
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full border transition-colors duration-300"
                  style={{
                    borderColor: reach > 0.5 ? "transparent" : "var(--border)",
                    background: reach > 0.5 ? "var(--primary)" : "transparent",
                  }}
                >
                  <Check
                    size={10}
                    strokeWidth={2.5}
                    className="text-primary-foreground"
                    style={{ opacity: clamp01((reach - 0.5) / 0.3) }}
                  />
                </span>
                <span
                  className="whitespace-nowrap text-[9px] text-muted-foreground"
                  style={{ opacity: 0.4 + reach * 0.6 }}
                >
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <span className="relative mx-1 h-px flex-1 bg-border">
                  <span
                    className="absolute inset-y-0 left-0 bg-primary/60"
                    style={{ width: `${clamp01((q - (i + 0.5) / steps.length) * steps.length) * 100}%` }}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 8 · SPRACHE / TRANSKRIPT ═══════════════════════════════════════════
// Die Wellenform ist deterministisch aus Index + p — beim Spulen identisch,
// aber im Lauf lebendig.

export function WWave({
  p, app, Icon, title, lines,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; lines: { who: string; text: string }[];
}) {
  const q = clamp01((p - 0.15) / 0.75);
  const bars = 34;
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 flex h-8 items-center gap-[2px]" aria-hidden="true">
        {Array.from({ length: bars }, (_, i) => {
          const live = i / bars < q;
          const amp = live
            ? 0.25 + Math.abs(Math.sin(i * 0.7 + q * 26)) * 0.75 * (0.5 + noise(i) * 0.5)
            : 0.08;
          return (
            <span
              key={i}
              className="flex-1 rounded-full"
              style={{
                height: `${amp * 100}%`,
                background: live ? "var(--foreground)" : "var(--border)",
                opacity: live ? 0.55 : 1,
              }}
            />
          );
        })}
      </div>
      <div className="mt-2 space-y-1">
        {lines.map((l, i) => {
          const s = stagger(q, i, lines.length, 0.6);
          return (
            <p
              key={l.text}
              className="text-[11px] leading-snug"
              style={{ opacity: easeOut(s / 0.4), transform: `translateY(${(1 - easeOut(s / 0.4)) * 4}px)` }}
            >
              <span className="font-mono text-[10px] text-muted-foreground/60">{l.who}: </span>
              <span className="text-muted-foreground">{l.text}</span>
            </p>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 9 · CHIPS ══════════════════════════════════════════════════════════

export function WChips({
  p, app, Icon, title, chips, tone = "neutral",
}: {
  p: number; app: string; Icon: LucideIcon; title: string; chips: string[];
  tone?: "neutral" | "mark";
}) {
  const q = clamp01((p - 0.2) / 0.7);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {chips.map((c, i) => {
          const s = easeOut(stagger(q, i, chips.length, 0.65) / 0.4);
          return (
            <span
              key={c}
              className={`rounded-full border px-2 py-0.5 text-[10px] ${
                tone === "mark"
                  ? "border-primary/40 bg-primary/[0.06] font-medium"
                  : "border-border text-muted-foreground"
              }`}
              style={{ opacity: s, transform: `translateY(${(1 - s) * 6}px) scale(${0.92 + s * 0.08})` }}
            >
              {c}
            </span>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 10 · DOKUMENT-SCAN ═════════════════════════════════════════════════
// Ein Leuchtbalken faehrt einmal durch das Dokument; was er passiert hat,
// bleibt markiert. Das macht "gelesen" sichtbar, ohne es zu behaupten.

export function WScan({
  p, app, Icon, title, rows,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  rows: { text: string; mark?: boolean }[];
}) {
  const q = clamp01((p - 0.16) / 0.66);
  const head = easeInOut(q) * rows.length;
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="relative mt-2 space-y-1 overflow-hidden">
        {rows.map((r, i) => {
          const passed = head > i + 0.7;
          const under = head > i && head <= i + 1.2;
          return (
            <div
              key={r.text}
              className="rounded-[4px] px-1 py-0.5 text-[11px] leading-snug transition-colors duration-300"
              style={{
                background: under
                  ? "color-mix(in srgb, var(--foreground) 9%, transparent)"
                  : passed && r.mark
                    ? "color-mix(in srgb, var(--foreground) 5%, transparent)"
                    : "transparent",
                color: passed && r.mark ? "var(--foreground)" : "var(--muted-foreground)",
                fontWeight: passed && r.mark ? 500 : 400,
                opacity: head > i - 0.5 ? 1 : 0.35,
              }}
            >
              {r.mark && passed && <span className="mr-1 text-foreground">▍</span>}
              {r.text}
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ── Jane-Marke ─────────────────────────────────────────────────────────
// Wenn Jane von sich aus handelt, muss sie als Absender sichtbar sein. Ohne
// Gesicht bleibt "die KI hat sich gemeldet" abstrakt.

export function JaneMark({ size = 20 }: { size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground"
      style={{ width: size, height: size, fontSize: size * 0.46 }}
      aria-hidden="true"
    >
      J
    </span>
  );
}

// ══ 13 · MESSENGER-THREAD ══════════════════════════════════════════════
// Jane meldet sich AUSSERHALB der Suite — ueber den Messenger-Bridge-Kanal.
// Genau das ist der "echter Kollege"-Beweis: man muss nicht in die App gehen,
// um zu erfahren, was lief.

export function WChat({
  p, app, Icon, title, channel, bubbles,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; channel: string;
  bubbles: { from: "jane" | "you"; text: string; stat?: string }[];
}) {
  const q = clamp01((p - 0.16) / 0.72);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <p className="mt-1 font-mono text-[10px] text-muted-foreground/60">{channel}</p>
      <div className="mt-2 space-y-2">
        {bubbles.map((b, i) => {
          const s = stagger(q, i, bubbles.length, 0.62);
          const typing = s > 0.02 && s < 0.3 && b.from === "jane";
          const shown = easeOut(clamp01((s - 0.26) / 0.4));
          if (typing) {
            return (
              <div key={b.text} className="flex items-end gap-1.5">
                <JaneMark size={18} />
                <span className="flex gap-1 rounded-[10px] rounded-bl-[3px] border border-border bg-muted/50 px-2.5 py-2">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="h-1 w-1 rounded-full bg-muted-foreground"
                      style={{ opacity: 0.3 + 0.7 * ((Math.sin(p * 26 + d * 1.1) + 1) / 2) }}
                    />
                  ))}
                </span>
              </div>
            );
          }
          if (shown <= 0) return <div key={b.text} className="h-0" />;
          const mine = b.from === "you";
          return (
            <div
              key={b.text}
              className={`flex items-end gap-1.5 ${mine ? "flex-row-reverse" : ""}`}
              style={{ opacity: shown, transform: `translateY(${(1 - shown) * 8}px)` }}
            >
              {!mine && <JaneMark size={18} />}
              <span
                className={`max-w-[82%] rounded-[10px] px-2.5 py-1.5 text-[11px] leading-snug ${
                  mine
                    ? "rounded-br-[3px] bg-primary text-primary-foreground"
                    : "rounded-bl-[3px] border border-border bg-muted/40"
                }`}
              >
                {b.text}
                {b.stat && (
                  <span className="mt-1 block font-mono text-[10px] opacity-70">{b.stat}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 14 · WISSENSLÜCKE ══════════════════════════════════════════════════
// Jane wertet ihre EIGENEN Fehlschlaege aus. Der Zaehler ist das Argument:
// nicht ein Aussetzer, sondern ein Muster — deshalb lohnt die Rueckfrage.

export function WGap({
  p, app, Icon, title, question, hits,
}: {
  p: number; app: string; Icon: LucideIcon; title: string; question: string;
  hits: { Icon: LucideIcon; where: string; when: string }[];
}) {
  const q = clamp01((p - 0.18) / 0.7);
  const counted = hits.filter((_, i) => stagger(q, i, hits.length, 0.7) > 0.3).length;
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="font-mono text-2xl font-semibold tabular-nums leading-none">
          {counted}×
        </span>
        <span className="text-[11px] text-muted-foreground">nicht beantwortet</span>
      </div>
      <p className="mt-1.5 rounded-[5px] border-l-2 border-primary/40 bg-primary/[0.04] px-2 py-1 text-[11px] italic leading-snug">
        „{question}“
      </p>
      <div className="mt-2 space-y-1">
        {hits.map((h, i) => {
          const s = easeOut(stagger(q, i, hits.length, 0.7) / 0.45);
          return (
            <div
              key={h.when}
              className="flex items-center gap-2 text-[10px] text-muted-foreground"
              style={{ opacity: s, transform: `translateX(${(1 - s) * 8}px)` }}
            >
              <h.Icon size={11} strokeWidth={2.25} className="shrink-0" />
              <span>{h.where}</span>
              <span className="ml-auto font-mono text-muted-foreground/60">{h.when}</span>
            </div>
          );
        })}
      </div>
    </Widget>
  );
}

// ══ 15 · RÜCKFRAGE & LERNEN ════════════════════════════════════════════
// Der Moment, in dem aus einem Werkzeug ein Mitarbeiter wird: Jane fragt,
// bekommt die Antwort einmal — und kann es ab dann selbst.

export function WAsk({
  p, app, Icon, title, question, answer, effect,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  question: string; answer: string; effect: string;
}) {
  const ask = clamp01((p - 0.16) / 0.2);
  const typeQ = clamp01((p - 0.34) / 0.3);
  const learned = p > 0.76;
  const n = Math.floor(easeOut(typeQ) * answer.length);

  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2 flex items-start gap-1.5" style={{ opacity: ask }}>
        <JaneMark size={18} />
        <span className="max-w-[88%] rounded-[10px] rounded-bl-[3px] border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] leading-snug">
          {question}
        </span>
      </div>

      {/* Deine Antwort — tippt sich ein, damit klar ist: DU lieferst hier. */}
      <div
        className="mt-2 rounded-[var(--radius)] border px-2.5 py-1.5"
        style={{
          opacity: clamp01((p - 0.32) / 0.12),
          borderColor: learned ? "var(--border)" : "var(--foreground)",
        }}
      >
        <p className="text-[11px] leading-snug">
          {answer.slice(0, n)}
          {n < answer.length && (
            <span className="ml-px inline-block h-[0.9em] w-[2px] translate-y-[0.1em] bg-primary/70" />
          )}
        </p>
      </div>

      <div
        className="mt-2 flex items-center gap-1.5 text-[11px]"
        style={{
          opacity: clamp01((p - 0.78) / 0.16),
          transform: `translateY(${(1 - clamp01((p - 0.78) / 0.16)) * 6}px)`,
        }}
      >
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary">
          <Check size={9} strokeWidth={2.75} className="text-primary-foreground" />
        </span>
        <span className="font-medium">{effect}</span>
      </div>
    </Widget>
  );
}

// ══ 16 · WORKFLOW ══════════════════════════════════════════════════════
// Zwei Phasen in einem Widget: erst BAUT sich der Ablauf (Knoten erscheinen,
// Kanten wachsen), dann LAEUFT er einmal durch (ein Impuls wandert, jeder
// Knoten quittiert). Der Testlauf ist der Vertrauensbeweis — man sieht, dass
// die Automatisierung tut, was sie soll, bevor sie scharf geschaltet wird.

export function WWorkflow({
  p, app, Icon, title, nodes, runtime,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  nodes: { Icon: LucideIcon; label: string; meta: string }[];
  runtime: string;
}) {
  const build = clamp01((p - 0.12) / 0.42);
  const run = clamp01((p - 0.6) / 0.34);
  const head = run * nodes.length;

  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-2">
        {nodes.map((n, i) => {
          const s = easeOut(stagger(build, i, nodes.length, 0.72) / 0.5);
          const reached = head > i + 0.55;
          const active = head > i && head <= i + 1;
          const edge = i < nodes.length - 1
            ? easeOut(clamp01((stagger(build, i, nodes.length, 0.72) - 0.5) / 0.5))
            : 0;
          return (
            <div key={n.label}>
              <div
                className="flex items-center gap-2 rounded-[6px] border px-2 py-1.5 transition-colors duration-300"
                style={{
                  opacity: s,
                  transform: `translateX(${(1 - s) * 10}px)`,
                  borderColor: active || reached ? "var(--foreground)" : "var(--border)",
                  background: reached
                    ? "color-mix(in srgb, var(--foreground) 5%, transparent)"
                    : "transparent",
                  boxShadow: active
                    ? "0 0 18px -6px color-mix(in srgb, var(--foreground) 60%, transparent)"
                    : "none",
                }}
              >
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border"
                  style={{
                    borderColor: reached ? "transparent" : "var(--border)",
                    background: reached ? "var(--primary)" : "transparent",
                  }}
                >
                  {reached ? (
                    <Check size={10} strokeWidth={2.5} className="text-primary-foreground" />
                  ) : (
                    <n.Icon size={11} strokeWidth={2.25} className="text-muted-foreground" />
                  )}
                </span>
                <span className="truncate text-[11px] font-medium">{n.label}</span>
                <span className="ml-auto shrink-0 font-mono text-[9px] text-muted-foreground/60">
                  {n.meta}
                </span>
              </div>

              {/* Kante: waechst beim Bauen, fuellt sich beim Durchlauf. */}
              {i < nodes.length - 1 && (
                <div className="relative ml-[1.05rem] h-3 w-px bg-border" style={{ opacity: edge }}>
                  <span
                    className="absolute inset-x-0 top-0 bg-primary"
                    style={{ height: `${clamp01(head - i - 0.55) * 100}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p
        className="mt-2 font-mono text-[10px] text-muted-foreground"
        style={{ opacity: clamp01((p - 0.92) / 0.08) }}
      >
        Testlauf erfolgreich · {runtime}
      </p>
    </Widget>
  );
}

// ══ 12 · FREIGABE-TOR ══════════════════════════════════════════════════
// Der halbautomatische Fall. Wichtig ist die WARTEZEIT: Jane hat alles fertig
// und haelt an. Der pulsierende Ring laeuft, bis freigegeben wird — erst danach
// laufen die folgenden Beats. Ohne diese sichtbare Pause waere "wartet auf
// Bestaetigung" nur ein Wort auf einer Karte.

export function WConfirm({
  p, app, Icon, title, summary, approveLabel = "Freigeben", at,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  summary: string; approveLabel?: string; at: string;
}) {
  const approved = p > 0.62;
  const wait = clamp01((p - 0.2) / 0.42);
  // Pulsieren aus p ableiten, nicht aus einer CSS-Animation — sonst laeuft es
  // beim Spulen aus dem Takt.
  const pulse = approved ? 0 : (Math.sin(p * 34) + 1) / 2;

  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">{summary}</p>

      <div className="mt-2.5 flex items-center gap-2">
        <span
          className="relative rounded-[calc(var(--radius)-3px)] px-3 py-1.5 text-[11px] font-medium"
          style={{
            background: "var(--primary)",
            color: "var(--primary-foreground)",
            boxShadow: approved
              ? "none"
              : `0 0 0 ${2 + pulse * 4}px color-mix(in srgb, var(--foreground) ${8 + pulse * 10}%, transparent)`,
            opacity: 0.55 + wait * 0.45,
          }}
        >
          {approved ? "Freigegeben" : approveLabel}
        </span>
        <span
          className="rounded-[calc(var(--radius)-3px)] border border-border px-3 py-1.5 text-[11px] text-muted-foreground"
          style={{ opacity: approved ? 0.3 : 0.55 + wait * 0.45 }}
        >
          Ändern
        </span>

        <span className="ml-auto font-mono text-[10px] text-muted-foreground/60">
          {approved ? at : "wartet auf dich"}
        </span>
      </div>
    </Widget>
  );
}

// ══ 11 · ÜBERSETZUNG ═══════════════════════════════════════════════════
// Quelle blendet nach oben weg, Ziel steigt nach — Wortpaar fuer Wortpaar
// versetzt. Der Wechsel ist die Animation, nicht ein Fade.

export function WTranslate({
  p, app, Icon, title, from, to, pairs,
}: {
  p: number; app: string; Icon: LucideIcon; title: string;
  from: string; to: string; pairs: { src: string; dst: string }[];
}) {
  const q = clamp01((p - 0.18) / 0.68);
  return (
    <Widget app={app} Icon={Icon} title={title} p={p}>
      <div className="mt-1.5 flex items-center gap-2 font-mono text-[10px] text-muted-foreground/70">
        <span>{from}</span>
        <span className="h-px flex-1 bg-border" />
        <span style={{ opacity: 0.35 + easeOut(q) * 0.65, color: "var(--foreground)" }}>{to}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {pairs.map((pr, i) => {
          const s = easeInOut(stagger(q, i, pairs.length, 0.6) / 0.55);
          return (
            <div key={pr.src} className="relative h-4 overflow-hidden">
              <p
                className="absolute inset-x-0 text-[11px] leading-4 text-muted-foreground"
                style={{ transform: `translateY(${-s * 100}%)`, opacity: 1 - s }}
              >
                {pr.src}
              </p>
              <p
                className="absolute inset-x-0 text-[11px] leading-4"
                style={{ transform: `translateY(${(1 - s) * 100}%)`, opacity: s }}
              >
                {pr.dst}
              </p>
            </div>
          );
        })}
      </div>
    </Widget>
  );
}
